import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Logo from './imagens/logo_original.png';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const error = useMemo(() => {
    if (!email) return 'Informe seu e-mail.';
    if (!emailRegex.test(email)) return 'Digite um e-mail válido.';
    return null;
  }, [email]);

  const canSubmit = !error && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (error) return;

    setSubmitting(true);
    setMessage(null);

    try {
      const res = await fetch('https://videooferta.com/api/esqueci-senha.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      // Mesmo que o endpoint ainda não exista, evitamos revelar se o e-mail é válido.
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      if (data?.success) {
        setMessage({
          type: 'success',
          text: 'Se existir uma conta com esse e-mail, enviaremos instruções para redefinir a senha.'
        });
      } else {
        setMessage({
          type: 'success',
          text: 'Se existir uma conta com esse e-mail, enviaremos instruções para redefinir a senha.'
        });
      }
    } catch {
      setMessage({
        type: 'info',
        text: 'Recuperação de senha indisponível no momento. Tente novamente mais tarde.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const showError = (touched && error) || false;

  return (
    <div className="relative min-h-screen bg-dot-grid overflow-hidden">
      <main className="relative mx-auto flex min-h-screen max-w-6xl flex-col items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="w-full max-w-md rounded-2xl border border-flow-border bg-white p-6 sm:p-8"
        >
          <div className="mb-6 flex items-center justify-center">
            <img src={Logo} alt="VídeoOferta" className="h-12 w-auto" />
          </div>

          <div className="mb-6 space-y-2">
            <h2 className="text-2xl font-semibold text-flow-ink">Recuperar senha</h2>
            <p className="text-sm text-flow-muted">
              Informe seu e-mail para receber instruções de redefinição.
            </p>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-flow-ink">
                E-mail
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-flow-ink placeholder-slate-400 outline-none transition focus-visible:ring-2 focus-visible:ring-flow-brand ${
                  showError
                    ? 'border-red-400/70 focus-visible:ring-red-400'
                    : 'border-flow-border focus-visible:border-flow-brand'
                }`}
                placeholder="voce@exemplo.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onBlur={() => setTouched(true)}
                aria-invalid={Boolean(showError)}
                aria-describedby={showError ? 'email-error' : undefined}
                required
              />
              {showError && (
                <p id="email-error" className="text-xs text-red-300">
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-flow-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-flow-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <span
                    className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                    aria-hidden="true"
                  />
                  Enviando...
                </>
              ) : (
                'Enviar instruções'
              )}
            </button>

            {message && (
              <div
                className={`rounded-xl border px-4 py-3 text-sm ${
                  message.type === 'success'
                    ? 'border-flow-brand/30 bg-flow-brand-soft text-flow-brand'
                    : 'border-slate-200 bg-slate-50 text-slate-700'
                }`}
                role="status"
                aria-live="polite"
              >
                {message.text}
              </div>
            )}

            <div className="flex items-center justify-between text-sm">
              <Link
                to="/entrar"
                className="font-medium text-flow-brand transition hover:text-flow-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand"
              >
                Voltar para entrar
              </Link>
              <Link
                to="/cadastrar"
                className="font-medium text-flow-brand transition hover:text-flow-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand"
              >
                Criar conta
              </Link>
            </div>
          </form>
        </motion.div>
      </main>
    </div>
  );
}

export default ForgotPassword;
